import type { CanvasSelection, PatternProject } from '../types';
import { MARD_MIXED_A1_M15_INDICES } from './palette';

export type PatternColorChoice = number | null;
export const MIXED_COLOR = 'mixed' as const;
export type PatternColorTarget = PatternColorChoice | typeof MIXED_COLOR;
export type PatternReplaceScope = 'all' | 'selection';

function matchesChoice(project: PatternProject, index: number, choice: PatternColorChoice) {
  return choice === null ? project.empty[index] !== 0 : !project.empty[index] && project.colorIndices[index] === choice;
}

function isInsideSelection(project: PatternProject, index: number, selection: CanvasSelection | null, scope: PatternReplaceScope) {
  if (scope === 'all') return true;
  if (!selection) return false;
  const col = index % project.width;
  const row = Math.floor(index / project.width);
  return col >= Math.min(selection.startCol, selection.endCol)
    && col <= Math.max(selection.startCol, selection.endCol)
    && row >= Math.min(selection.startRow, selection.endRow)
    && row <= Math.max(selection.startRow, selection.endRow);
}

export function getPatternReplaceCount(
  project: PatternProject,
  source: PatternColorChoice,
  scope: PatternReplaceScope,
  selection: CanvasSelection | null
) {
  if (scope === 'selection' && !selection) return 0;
  let count = 0;
  for (let index = 0; index < project.colorIndices.length; index += 1) {
    if (isInsideSelection(project, index, selection, scope) && matchesChoice(project, index, source)) count += 1;
  }
  return count;
}

export function replacePatternColor(
  project: PatternProject,
  source: PatternColorChoice,
  target: PatternColorTarget,
  scope: PatternReplaceScope,
  selection: CanvasSelection | null,
  random: () => number = Math.random
) {
  if (source === target) return { project, changed: 0 };
  const mixedCandidates = target === MIXED_COLOR
    ? MARD_MIXED_A1_M15_INDICES.filter((colorIndex) => colorIndex !== source)
    : [];
  if (target === MIXED_COLOR && !mixedCandidates.length) return { project, changed: 0 };
  const colorIndices = project.colorIndices.slice();
  const empty = project.empty.slice();
  const completed = project.completed.slice();
  let changed = 0;

  for (let index = 0; index < colorIndices.length; index += 1) {
    if (!isInsideSelection(project, index, selection, scope) || !matchesChoice(project, index, source)) continue;
    if (target === null) {
      colorIndices[index] = 0;
      empty[index] = 1;
    } else if (target === MIXED_COLOR) {
      const randomValue = random();
      const normalized = Number.isFinite(randomValue) ? Math.min(0.999999, Math.max(0, randomValue)) : 0;
      colorIndices[index] = mixedCandidates[Math.floor(normalized * mixedCandidates.length)];
      empty[index] = 0;
    } else {
      colorIndices[index] = target;
      empty[index] = 0;
    }
    completed[index] = 0;
    changed += 1;
  }

  if (!changed) return { project, changed: 0 };
  return {
    project: { ...project, colorIndices, empty, completed, updatedAt: Date.now() },
    changed
  };
}
